import { useState } from 'react'
import { Check, ChevronDown, ChevronUp, Send } from 'lucide-react'

/**
 * PromoteEntryButton — Promuovi entry dal diario alla timeline PEP
 *
 * Stato default: bottone tratteggiato "Aggiungi alla timeline".
 * Click: espande form inline con tipo, titolo, nota, indicatore, visibilità.
 * Conferma: POST /api/pep/:pepId/eventi con entryId.
 *
 * Props:
 *   entryId          - ObjectId della entry
 *   pepId            - ObjectId del PEP attivo
 *   entryDate        - Data dell'entry (per il campo dataEvento)
 *   entryRiepilogo   - Stringa per pre-compilare il titolo
 *   isAlreadyPromoted - Boolean, se true bottone verde non cliccabile
 *   onPromoted       - Callback dopo promozione riuscita
 */

const TIPO_OPTIONS = [
  { value: 'evento_significativo', label: 'Evento significativo' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'nota_coordinatore', label: 'Nota coordinatore' }
]

const INDICATORE_OPTIONS = [
  { value: 'positivo', label: 'Positivo', color: 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700' },
  { value: 'neutro', label: 'Neutro', color: 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-900/20 dark:border-gray-600' },
  { value: 'attenzione', label: 'Attenzione', color: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-900/20 dark:border-orange-700' }
]

const VISIBILITA_OPTIONS = [
  { value: 'tutti', label: 'Tutti' },
  { value: 'solo_coordinatore', label: 'Solo coordinatore' }
]

export default function PromoteEntryButton({
  entryId,
  pepId,
  entryDate,
  entryRiepilogo = '',
  isAlreadyPromoted = false,
  onPromoted
}) {
  const [expanded, setExpanded] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [promoted, setPromoted] = useState(isAlreadyPromoted)

  const [tipo, setTipo] = useState('evento_significativo')
  const [titolo, setTitolo] = useState(entryRiepilogo)
  const [nota, setNota] = useState('')
  const [indicatore, setIndicatore] = useState('neutro')
  const [visibilita, setVisibilita] = useState('tutti')

  // Già promossa — stato finale
  if (promoted) {
    return (
      <div className="flex items-center gap-1.5 text-green-600 text-xs font-medium py-1">
        <Check className="h-3.5 w-3.5" />
        Aggiunto alla timeline
      </div>
    )
  }

  const handleSubmit = async () => {
    if (!titolo.trim()) return
    setSubmitting(true)
    try {
      const { pepApi } = await import('../../services/api')
      await pepApi.addEvento(pepId, {
        dataEvento: entryDate,
        tipo,
        titolo: titolo.trim(),
        descrizione: nota.trim() || undefined,
        indicatore,
        visibilita,
        entryId
      })
      setPromoted(true)
      setExpanded(false)
      onPromoted?.()
    } catch (error) {
      // Se l'entry è già stata promossa (409), aggiorna lo stato
      if (error.response?.status === 409) {
        setPromoted(true)
        setExpanded(false)
      }
      // Altrimenti l'errore si propaga al chiamante
    } finally {
      setSubmitting(false)
    }
  }

  // Bottone compatto (non espanso)
  if (!expanded) {
    return (
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpanded(true) }}
        className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 border border-dashed border-primary-300 dark:border-primary-600 rounded-md px-2.5 py-1.5 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        <span>📌</span>
        Aggiungi alla timeline
      </button>
    )
  }

  // Form inline espanso
  return (
    <div
      className="mt-2 border border-primary-200 dark:border-primary-700 rounded-lg bg-white dark:bg-gray-800 p-3 space-y-3"
      onClick={(e) => { e.preventDefault(); e.stopPropagation() }}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
          📌 Promuovi alla timeline
        </span>
        <button
          onClick={() => setExpanded(false)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
      </div>

      {/* Tipo evento */}
      <div>
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
          Tipo evento
        </label>
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          {TIPO_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Titolo */}
      <div>
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
          Titolo *
        </label>
        <input
          type="text"
          value={titolo}
          onChange={(e) => setTitolo(e.target.value)}
          maxLength={200}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          placeholder="Titolo per la timeline"
        />
      </div>

      {/* Nota aggiuntiva */}
      <div>
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
          Nota aggiuntiva
        </label>
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          maxLength={1000}
          rows={2}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none"
          placeholder="Note aggiuntive (opzionale)"
        />
      </div>

      {/* Indicatore */}
      <div>
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
          Indicatore
        </label>
        <div className="flex gap-2">
          {INDICATORE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setIndicatore(opt.value)}
              className={`flex-1 text-xs font-medium py-1.5 rounded-md border transition-colors ${
                indicatore === opt.value
                  ? opt.color + ' ring-1 ring-current'
                  : 'text-gray-400 bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-500'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Visibilità */}
      <div>
        <label className="block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1">
          Visibilità
        </label>
        <select
          value={visibilita}
          onChange={(e) => setVisibilita(e.target.value)}
          className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        >
          {VISIBILITA_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* Azioni */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={submitting || !titolo.trim()}
          className="flex items-center gap-1.5 text-xs font-medium bg-primary-600 text-white px-3 py-1.5 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? (
            <div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Conferma
        </button>
        <button
          onClick={() => setExpanded(false)}
          disabled={submitting}
          className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-2 py-1.5"
        >
          Annulla
        </button>
      </div>
    </div>
  )
}
